import { PartexVehicleVariant } from '@cinch-labs/data-consumer-valuations'
import {
  AppointmentType,
  ContactTimePreference,
  CustomerAddress,
  ServiceOptions,
} from '../types'

export interface MaintenanceState {
  step: number
  customerTitle?: string
  customerFirstName?: string
  customerSurname?: string
  customerEmail?: string
  customerAddress?: CustomerAddress
  store?: string
  storeId?: string
  displayName?: string
  vehicleMake?: string
  vehicleModel?: string
  vehicleVin?: string
  vehicleFuelType?: string
  vehicleAgeInMonths?: number
  under60Months?: boolean
  vrm?: string
  mileage?: number
  notes?: string
  serviceOptions?: ServiceOptions
  serviceOptionsSelectedNames?: string
  serviceOptionsSelectedTotalPrice?: string
  streetAddress?: string
  postcode?: string
  customerPhone?: string
  bookingDate?: string
  bookingTime?: string
  appointmentType?: AppointmentType
  appointmentTypeId?: number
  foundVariants?: PartexVehicleVariant[]
  searchedVrm?: string
  selectedFoundVariantId?: string | null
  emailPermissions?: boolean
  contactTimePreference?: ContactTimePreference
  canBookAppointment?: boolean
  dealerId?: string
  dealerCode?: string
}

export type MaintenanceAction =
  | {
      type: 'SET_SELECTED_SERVICE_OPTIONS'
      serviceOptions: ServiceOptions
      serviceOptionsSelectedNames: string
      serviceOptionsSelectedTotalPrice: string
    }
  | { type: 'NEXT_STEP' }
  | { type: 'PREVIOUS_STEP' }
  | { type: 'SET_STORE'; name: string; id: string; displayName: string }
  | {
      type: 'SET_VEHICLE'
      make: string
      model: string
      vrm: string
      vin?: string
      mileage?: number
      fuelType?: string
    }
  | {
      type: 'SET_POSTCODE'
      postcode: string
    }
  | {
      type: 'SET_VEHICLE_AGE_IN_MONTHS'
      vehicleAgeInMonths?: number
    }
  | { type: 'SET_NOTES'; notes: string }
  | { type: 'UPDATE_NOTES'; notes: string }
  | {
      type: 'SET_CUSTOMER_DETAILS'
      phone: string
      email: string
      title?: string
      firstName: string
      lastName: string
      streetAddress?: string
      postcode: string
      customerAddress?: CustomerAddress
      contactTimePreference: ContactTimePreference
      emailPermissions?: boolean
    }
  | {
      type: 'SET_BOOKING_DATETIME'
      bookingDate?: string
      bookingTime?: string
      appointmentType: AppointmentType
      appointmentTypeId?: number
    }
  | {
      type: 'SET_FOUND_VARIANTS'
      foundVariants: PartexVehicleVariant[]
      mileage?: number
    }
  | { type: 'CLEAR_FOUND_VARIANTS' }
  | { type: 'SET_SEARCHED_VRM'; searchedVrm: string }
  | { type: 'SET_SELECTED_FOUND_VARIANT_ID'; id: string | null }
  | { type: 'SET_STEP'; step: number }
  | { type: 'SET_ENQUIRY_SUBMITTED' }
  | { type: 'DELETE_SLOT' }
  | { type: 'SET_CAN_BOOK_APPOINTMENT'; canBookAppointment: boolean }
  | { type: 'SET_DEALER_ID'; dealerId: string }
  | { type: 'SET_DEALER_CODE'; dealerCode: string }
  | {
      type: 'SET_CUSTOMER_ADDRESS'
      addressLine1: string
      city: string
      county: string
    }

export const MaintenanceReducer = (
  state: MaintenanceState,
  action: MaintenanceAction,
) => {
  switch (action.type) {
    case 'SET_SELECTED_SERVICE_OPTIONS': {
      return {
        ...state,
        serviceOptions: action.serviceOptions,
        serviceOptionsSelectedNames: action.serviceOptionsSelectedNames,
        serviceOptionsSelectedTotalPrice:
          action.serviceOptionsSelectedTotalPrice,
      }
    }
    case 'NEXT_STEP': {
      return {
        ...state,
        step: state.step + 1,
      }
    }
    case 'SET_STEP': {
      return {
        ...state,
        step: action.step,
      }
    }
    case 'PREVIOUS_STEP': {
      return {
        ...state,
        step: state.step > 1 ? state.step - 1 : 1,
      }
    }
    case 'SET_STORE': {
      return {
        ...state,
        store: action.name,
        storeId: action.id,
        displayName: action.displayName,
        step: 3,
      }
    }
    case 'SET_VEHICLE': {
      return {
        ...state,
        vehicleMake: action.make,
        vehicleModel: action.model,
        vrm: action.vrm,
        vehicleVin: action.vin,
        vehicleFuelType: action.fuelType,
      }
    }
    case 'SET_POSTCODE': {
      return {
        ...state,
        postcode: action.postcode,
      }
    }
    case 'SET_VEHICLE_AGE_IN_MONTHS': {
      return {
        ...state,
        vehicleAgeInMonths: action.vehicleAgeInMonths,
        under60Months:
          action.vehicleAgeInMonths !== undefined
            ? action.vehicleAgeInMonths < 60
            : false,
      }
    }
    case 'SET_BOOKING_DATETIME': {
      return {
        ...state,
        bookingDate: action.bookingDate,
        bookingTime: action.bookingTime,
        appointmentType: action.appointmentType,
        appointmentTypeId: action.appointmentTypeId,
        step: 5,
      }
    }
    case 'UPDATE_NOTES': {
      return {
        ...state,
        notes: action.notes,
      }
    }
    case 'SET_NOTES': {
      return {
        ...state,
        notes: action.notes,
        step: 6,
      }
    }
    case 'SET_CUSTOMER_DETAILS': {
      return {
        ...state,
        customerTitle: action.title,
        customerPhone: action.phone,
        customerEmail: action.email,
        customerFirstName: action.firstName,
        customerSurname: action.lastName,
        streetAddress: action.streetAddress,
        postcode: action.postcode,
        customerAddress: action.customerAddress,
        contactTimePreference: action.contactTimePreference,
        emailPermissions: action.emailPermissions,
        step: 6,
      }
    }
    case 'SET_CUSTOMER_ADDRESS': {
      return {
        ...state,
        street: action.addressLine1,
        town: action.city,
        county: action.county,
      }
    }
    case 'SET_FOUND_VARIANTS': {
      return {
        ...state,
        foundVariants: action.foundVariants,
        mileage: action.mileage,
      }
    }
    case 'CLEAR_FOUND_VARIANTS': {
      return {
        ...state,
        foundVariants: [],
      }
    }
    case 'SET_SEARCHED_VRM': {
      return {
        ...state,
        searchedVrm: action.searchedVrm,
      }
    }
    case 'SET_SELECTED_FOUND_VARIANT_ID': {
      return {
        ...state,
        selectedFoundVariantId: action.id ?? null,
      }
    }
    case 'DELETE_SLOT': {
      return {
        ...state,
        bookingDateTime: undefined,
        bookingDate: undefined,
        bookingStartTime: undefined,
        bookingEndTime: undefined,
        step: 4,
      }
    }
    case 'SET_ENQUIRY_SUBMITTED': {
      return {
        ...state,
        step: 7,
      }
    }
    case 'SET_CAN_BOOK_APPOINTMENT': {
      return {
        ...state,
        canBookAppointment: action.canBookAppointment,
      }
    }
    case 'SET_DEALER_ID': {
      return {
        ...state,
        dealerId: action.dealerId,
      }
    }
    case 'SET_DEALER_CODE': {
      return {
        ...state,
        dealerCode: action.dealerCode,
      }
    }
    default: {
      return state
    }
  }
}
